// =============================================================================
// BACK TO TOP COMPONENT
// =============================================================================

import React, { useState, useEffect } from 'react';
import { ChevronUp } from 'lucide-react';

interface BackToTopProps {
  /**
   * Scroll threshold in pixels before button appears
   * @default 300
   */
  threshold?: number;
  
  /**
   * Scroll behavior
   * @default 'smooth'
   */
  behavior?: ScrollBehavior;
  
  /**
   * Custom className for styling
   */
  className?: string;
  
  /**
   * Button size
   * @default 'md'
   */
  size?: 'sm' | 'md' | 'lg';
  
  /**
   * Button variant
   * @default 'primary'
   */
  variant?: 'primary' | 'secondary' | 'outline';
}

export const BackToTop: React.FC<BackToTopProps> = ({
  threshold = 300,
  behavior = 'smooth',
  className = '',
  size = 'md',
  variant = 'primary'
}) => {
  const [isVisible, setIsVisible] = useState(false);

  // Show/hide button based on scroll position
  useEffect(() => {
    const toggleVisibility = () => {
      if (window.pageYOffset > threshold) {
        setIsVisible(true);
      } else {
        setIsVisible(false);
      }
    };

    // Add scroll event listener
    window.addEventListener('scroll', toggleVisibility);

    // Cleanup
    return () => {
      window.removeEventListener('scroll', toggleVisibility);
    };
  }, [threshold]);

  // Scroll to top function
  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: behavior
    });
  };

  // Don't render if not visible
  if (!isVisible) {
    return null;
  }

  return (
    <button
      onClick={scrollToTop}
      className={`back-to-top back-to-top--${size} back-to-top--${variant} ${className}`}
      aria-label="Back to top"
      title="Back to top"
    >
      <ChevronUp size={size === 'sm' ? 16 : size === 'lg' ? 24 : 20} />
    </button>
  );
};

